import { RecursivePartial, RenderState, View } from "@novorender/api";

/**
 * Debug utility to log render state changes for troubleshooting
 * object visibility, loading, and rendering issues.
 */

let lastRenderState: RenderState | null = null;
let changeCounter = 0;

export function logRenderStateChange(view: View, change: RecursivePartial<RenderState>, source: string) {
    changeCounter++;

    const timestamp = new Date().toISOString().split("T")[1];
    console.group(`[RENDER STATE #${changeCounter}] ${timestamp} - ${source}`);

    // Log what changed
    console.log("Changes requested:", change);

    // Log clipping changes (objects disappearing might be clipped)
    if (change.clipping) {
        console.log("🔪 Clipping planes changed:", {
            enabled: change.clipping.enabled,
            planes: change.clipping.planes?.length,
            planeDetails: change.clipping.planes,
        });
    }

    // Log camera changes (frustum culling)
    if (change.camera) {
        console.log("📷 Camera changed:", {
            kind: change.camera.kind,
            position: change.camera.position,
            far: change.camera.far,
            near: change.camera.near,
        });
    }

    // Log background/environment (lighting affects visibility)
    if (change.background) {
        console.log("🎨 Background changed:", {
            color: change.background.color,
            url: change.background.url,
            blur: change.background.blur,
        });
    }

    // Log highlights (hidden objects)
    if (change.highlights) {
        console.log("✨ Highlights changed:", {
            defaultAction: change.highlights?.defaultAction,
            groups: change.highlights?.groups?.length,
            groupDetails: change.highlights?.groups
                ?.map((g) => {
                    if (!g) {
                        return null;
                    }
                    return {
                        action: g.action,
                        objectCount: Array.isArray(g.objectIds) ? g.objectIds.length : 0,
                    };
                })
                .filter(Boolean),
        });
    }

    // Log subtrees (hierarchy visibility)
    if ("subtrees" in change && change.subtrees) {
        console.log("🌳 Subtrees changed:", change.subtrees);
    }

    // Get current render state after change
    const currentState = view.renderState;

    // Detect significant changes
    if (lastRenderState) {
        // Check if objects might have been hidden
        const clippingChanged = JSON.stringify(lastRenderState.clipping) !== JSON.stringify(currentState.clipping);
        const highlightsChanged =
            JSON.stringify(lastRenderState.highlights) !== JSON.stringify(currentState.highlights);

        if (clippingChanged) {
            console.warn("⚠️ Clipping changed - objects might be clipped out");
        }
        if (highlightsChanged) {
            console.warn("⚠️ Highlights changed - objects might be hidden");
        }
    }

    // Track memory/performance
    const perfMemory = performance as unknown as { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } };
    if (perfMemory.memory) {
        const mem = perfMemory.memory;
        const usedMB = (mem.usedJSHeapSize / 1024 / 1024).toFixed(2);
        const limitMB = (mem.jsHeapSizeLimit / 1024 / 1024).toFixed(2);
        console.log(`💾 Memory: ${usedMB}MB / ${limitMB}MB`);
    }

    console.groupEnd();

    lastRenderState = JSON.parse(JSON.stringify(currentState));
}

/**
 * Wrap View.modifyRenderState to automatically log changes
 */
export function wrapViewForDebugging(view: View, enable: boolean = true) {
    if (!enable) {
        return;
    }

    const originalModify = view.modifyRenderState.bind(view);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (view as any).modifyRenderState = function (changes: RecursivePartial<RenderState>) {
        // Get caller info from stack trace
        const stack = new Error().stack;
        const caller = stack?.split("\n")[2]?.trim() || "unknown";

        logRenderStateChange(view, changes, caller);
        return originalModify(changes);
    };

    console.log("🐛 [DEBUG] View.modifyRenderState wrapped for debugging");
}

/**
 * Enable/disable debug logging (requires page reload to take effect)
 *
 * Usage:
 *   window.setRenderDebugMode(true);   // Enable
 *   location.reload();                  // Reload to apply
 *
 *   window.setRenderDebugMode(false);  // Disable
 *   location.reload();                  // Reload to apply
 */
export function setRenderDebugMode(enabled: boolean) {
    // Store in localStorage for persistence across reloads
    if (enabled) {
        localStorage.setItem("__RENDER_DEBUG_MODE__", "true");
        console.log("✅ [DEBUG] Render state debugging will be ENABLED on next page load");
    } else {
        localStorage.removeItem("__RENDER_DEBUG_MODE__");
        console.log("❌ [DEBUG] Render state debugging will be DISABLED on next page load");
    }

    // Also set on window for immediate access (though View wrapper won't be affected until reload)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__RENDER_DEBUG_MODE__ = enabled;

    console.log("↻ Please reload the page for changes to take effect: location.reload()");
}

// Expose globally for console access
if (typeof window !== "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).setRenderDebugMode = setRenderDebugMode;
}
